import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Code from "@tiptap/extension-code";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";

import { MathField } from "./extensions/math-field";
import { ResizableImage } from "./extensions/resizable-image";
import { codeBlock } from "./extensions/code-block";
import { FontSize } from "./extensions/font-size";
import { FontColor } from "./extensions/font-color";
import { Direction } from "./extensions/direction";
import { MermaidDiagram, renderMermaidDiagram } from "./extensions/mermaid-diagram";
import { BoardState, renderBoardBlock, ensureBoardLibrary } from "./extensions/board-state";
import { Panel, PanelRow, normalizePanelVariant } from "./extensions/panel";
import { cleanPastedHtml } from "./paste";
import { convertMathInHtml, hasMath, textWithMathToHtml } from "./math-paste";
import { DOMParser as PMDOMParser, type ResolvedPos, type Slice } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";
import { runCommand, changeFontSize as changeFontSizeCmd, tableCommand as tableCommandFn, activeCommands } from "./commands";

// Tiptap-based replacement for the old hand-rolled contenteditable editor (see the plan:
// C:\Users\gad_s\.claude\plans\quiet-mixing-balloon.md). Publishes as window.examinRichEditor —
// the SAME global name the old wwwroot/rich-content-editor.js used, so RichContentEditor.razor's
// JS.InvokeVoidAsync("examinRichEditor.X", ...) call sites keep working with only their JS-side
// implementation swapped out, not their C# call shape.

interface EditorState {
  editor: Editor;
  dotNetRef: any;
  notifyTimer: ReturnType<typeof setTimeout> | null;
  selectionTimer: ReturnType<typeof setTimeout> | null;
  lastActive: string | null;
  backupKey: string | null;
  backupTimer: ReturnType<typeof setTimeout> | null;
  markdownPaste: boolean;
  // Formulas between dollar signs (or \( \) and \[ \]) in pasted text become formulas, where the editor takes formulas.
  mathPaste: boolean;
  images: ImagePolicy;
}

// Where pictures may come from. "none": nowhere to keep one. "tools": only a picture copied from one
// of Examin's own tools - a locked exam. "paste": also one pasted from outside. "all": also a file.
type ImagePolicy = "none" | "tools" | "paste" | "all";

const asPolicy = (value: unknown): ImagePolicy =>
  value === true ? "all" : (value === "tools" || value === "paste" || value === "all" ? value : "none");

// A picture on the clipboard or dropped in - a screenshot, a copied image - but not the picture
// Word or Excel put there beside the text they copy: with text alongside, the text is what is meant.
function imageOf(data: DataTransfer | null): File | null {
  if (!data || (data.getData("text/plain") ?? "").trim()) return null;
  const item = Array.from(data.items ?? []).find((it) => it.kind === "file" && it.type.startsWith("image/"));
  return item?.getAsFile() ?? null;
}

// A picture copied from one of Examin's tools: the clipboard carries only its mark, and the picture
// waits in the page (tool-clipboard.js) - so it comes in even where nothing from outside may.
const TOOL_MARK = /^examin-tool-image:([a-z0-9]+)$/;

// A board copied from the board tool: its data waits in the page's own store, and only a mark is on
// the clipboard - so it reaches the answer in a locked exam too, as a picture does.
const TOOL_BOARD = /^examin-tool-board:([a-z0-9]+)$/;

function toolBoardOf(data: DataTransfer | null): { board: any; picture: Blob | null } | null {
  const id = TOOL_BOARD.exec((data?.getData("text/plain") ?? "").trim())?.[1];
  const board = id ? (window as any).examinToolClipboard?.getData(id) : null;
  return board ? { board, picture: (window as any).examinToolClipboard?.getBlob(id) ?? null } : null;
}

function toolImageOf(data: DataTransfer | null): Blob | null {
  const id = TOOL_MARK.exec((data?.getData("text/plain") ?? "").trim())?.[1];
  return id ? ((window as any).examinToolClipboard?.get(id) ?? null) : null;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// A photo from a phone runs to several megabytes. Anything larger than it needs to be is drawn
// again, its longer side at most 1600 pixels, as JPEG - or as PNG when small, which keeps a
// drawing's sharp lines. What stays over 5 MB is not sent.
const MAX_SIDE = 1600;
const MAX_BYTES = 5 * 1024 * 1024;

async function compress(blob: Blob): Promise<Blob | null> {
  if (blob.type === "image/gif" || blob.type === "image/svg+xml") return blob.size <= MAX_BYTES ? blob : null;
  try {
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && blob.size <= 1024 * 1024) { bitmap.close(); return blob; }
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const g = canvas.getContext("2d")!;
    g.fillStyle = "#fff";
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const png = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/png"));
    const out = png && png.size <= 512 * 1024 ? png : await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    return out && out.size <= MAX_BYTES ? out : null;
  } catch {
    return blob.size <= MAX_BYTES ? blob : null;
  }
}

// Saved by the server - with the activity's files, or with the student's answers - then put in at
// the caret, or where it was dropped. A failed upload leaves the editor as it was.
// The address of an image once the server has it, or null when it was refused or not kept.
async function storeImage(state: EditorState, image: Blob): Promise<string | null> {
  const small = await compress(image);
  if (!small) { state.dotNetRef.invokeMethodAsync("OnImageRefused"); return null; }
  return await state.dotNetRef.invokeMethodAsync("OnImagePasted", await blobToBase64(small), small.type);
}

async function uploadImage(state: EditorState, image: Blob, at?: number): Promise<void> {
  try {
    const url = await storeImage(state, image);
    if (!url) return;
    const chain = state.editor.chain().focus();
    if (at != null) chain.setTextSelection(at);
    chain.setImage({ src: url, alt: "" }).run();
    notify(state, true);
  } catch { /* nothing put in */ }
}

// Plain text that reads as Markdown: a heading, bold, a formula, a table row, a list. Such text
// pasted where the page allows it is converted, not dropped in with its marks showing.
const MARKDOWN_SIGNS = /^#{1,6}\s|^>\s*\[![A-Za-z]+\]|\*\*[^*\n]+\*\*|\$[^$\n]+\$|\$\$|\\\(|\\\[|^\s*\|.*\|\s*$|^\s*[-*+]\s+\S|^\s*\d+[.)]\s+\S|^```/m;

function looksLikeMarkdown(text: string): boolean {
  return MARKDOWN_SIGNS.test(text);
}

// Plain text that reads as code - copied from a compiler tool or an IDE. Pasted into a paragraph it
// would lose its indentation, so it goes in as a code block. Several lines, most of them with a
// sign of code: an indent, a line ending in ; { } or ), a keyword at the start, an operator.
const CODE_LINE = /^(?:\t| {2,})\S|[;{}]\s*$|[A-Za-z_]\w*\s*\(.*\)\s*:?\s*$|^\s*(?:def|class|return|if|elif|else|for|while|import|from|#include|using|public|private|static|void|int|double|float|char|bool|string|function|const|let|var|print|printf|cout|System\.|Console\.|console\.)\b|=>|==|!=|\+\+|<<|>>/;

// A rich paste that is a document - a table, a heading, a list, Word or Docs - is not code, even
// if its text happens to look like it. A code editor's own HTML is only colour, so it does not count.
const DOCUMENT_HTML = /<(?:table|h[1-6]|ul|ol|img)\b|mso-|docs-internal-guid/i;

export function looksLikeCode(text: string, html: string): boolean {
  if (html && DOCUMENT_HTML.test(html)) return false;
  if (/^\s*```/.test(text)) return false; // fenced Markdown: the Markdown paste has it
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length < 2) return false;
  // A line that is mostly Hebrew is prose - "שאלה 1 (20 נק')" - whatever its punctuation.
  const prose = (l: string) => (l.match(/[֐-׿]/g)?.length ?? 0) > (l.match(/[A-Za-z]/g)?.length ?? 0);
  const coded = lines.filter((l) => CODE_LINE.test(l) && !prose(l)).length;
  return coded >= 2 && coded / lines.length >= 0.5;
}

function guessLanguage(text: string): string | null {
  if (/^\s*#include\b|\bstd::|\bcout\s*<</m.test(text)) return "cpp";
  if (/\bSystem\.out\.|\bpublic\s+static\s+void\s+main\b/.test(text)) return "java";
  if (/\bConsole\.Write|^\s*using\s+System\b/m.test(text)) return "csharp";
  if (/^\s*(?:def|elif|import|from)\b|\bprint\(|:\s*$/m.test(text) && !/[;{}]\s*$/m.test(text)) return "python";
  if (/\bconsole\.log\b|\bfunction\b|=>|\b(?:const|let)\s+\w+\s*=/.test(text)) return "javascript";
  return null;
}

const instances = new Map<string, EditorState>();

function buildExtensions(placeholder?: string) {
  return [
    StarterKit.configure({ codeBlock: false, code: false }),
    codeBlock,
    // StarterKit's bundled Code mark ships with excludes: '_' (no other mark may coexist on
    // that text run) — by design, so inline code stays plain monospace. That silently blocked
    // FontColor (and bold/italic) from ever applying to code-marked text, with no toolbar way
    // to undo the code mark either. Cleared here so a colored inline-code paste can be recolored.
    Code.extend({ excludes: "" }),
    MathField,
    Underline,
    // Single-colour on purpose. Multicolour would need the colour on the tag and therefore
    // a style attribute through the server sanitizer; one <mark> styled by the theme keeps
    // highlighting consistent with the rest of the design system.
    Highlight,
    Subscript,
    Superscript,
    FontSize,
    FontColor,
    Direction,
    TextAlign.configure({ types: ["paragraph", "heading", "blockquote"] }),
    Table.configure({ resizable: false }),
    TableRow,
    TableCell,
    TableHeader,
    ResizableImage,
    MermaidDiagram,
    BoardState,
    Panel,
    PanelRow,
    Placeholder.configure({ placeholder: placeholder || "" }),
  ];
}

// Mirrors the old notify()'s debounce/immediate distinction: typing debounces (via the 'update'
// listener below), discrete actions (insert image/table/formula/code) call this with
// immediate=true right after performing the action, so clicking a toolbar button and then
// immediately navigating away (or clicking "Add question") never submits a stale value — this
// was a real bug in the old editor before that distinction existed.
function notify(state: EditorState, immediate = false): void {
  if (state.notifyTimer) clearTimeout(state.notifyTimer);
  const publish = () => state.dotNetRef?.invokeMethodAsync("OnEditorChanged", state.editor.getHTML());
  if (immediate) publish();
  else state.notifyTimer = setTimeout(publish, 450);
}

// Keeps a copy of what was typed in the browser itself, written here rather than from the server
// because the moment it matters is the moment the server cannot be reached: a connection that
// drops mid-answer loses what was typed after it, unless the page kept it. The page that owns the
// key decides what to do with the copy when it loads again.
function backup(state: EditorState): void {
  if (!state.backupKey) return;
  if (state.backupTimer) clearTimeout(state.backupTimer);
  state.backupTimer = setTimeout(() => {
    try { localStorage.setItem(state.backupKey!, state.editor.getHTML()); }
    catch { /* storage full or refused - the server copy is still the record */ }
  }, 250);
}

// Pushes which toolbar states are on, so the buttons can show it. Every push is a SignalR
// round trip on Blazor Server, and a caret moving through a paragraph fires selection events
// continuously, so this is both debounced and deduplicated: a move that does not change any
// state sends nothing at all, which is the common case while typing.
function publishActive(state: EditorState, immediate = false): void {
  if (state.selectionTimer) clearTimeout(state.selectionTimer);
  const send = () => {
    const keys = activeCommands(state.editor);
    const signature = keys.join("|");
    if (signature === state.lastActive) return;
    state.lastActive = signature;
    state.dotNetRef?.invokeMethodAsync("OnSelectionChanged", keys);
  };
  if (immediate) send();
  else state.selectionTimer = setTimeout(send, 120);
}

// What a display needs to draw its boards: whether the first one is a task for the student (and who to
// tell when they change it), and the values to set on it.
interface HydrateBoards { task?: boolean; values?: string | null; dotNet?: any }

function hydrateStatic(containerId: string, boards?: HydrateBoards): void {
  const root = document.getElementById(containerId);
  if (!root) return;
  customElements.whenDefined("math-field").then(() => {
    root.querySelectorAll("math-field[data-latex]").forEach((field: any) => {
      field.value = field.getAttribute("data-latex") || "";
      field.readOnly = true;
    });
  });
  root.querySelectorAll<HTMLElement>(".mermaid-diagram-block[data-mermaid-source]").forEach((el) => {
    // Read-only: nobody here can fix a broken diagram, so a failure hides it instead.
    renderMermaidDiagram(el, el.getAttribute("data-mermaid-source") || "", { silent: true });
  });
  // Only the first board a teacher made for the student to work takes the student's values: one task board
  // to a question. Any other board is shown still.
  let taskTaken = false;
  root.querySelectorAll<HTMLElement>("div[data-board-state]").forEach((el) => {
    const raw = el.getAttribute("data-board-state") || "";
    let isTaskBlock = false;
    try { isTaskBlock = JSON.parse(raw).mode === "task"; } catch { /* not a board */ }
    const task = Boolean(boards?.task) && isTaskBlock && !taskTaken;
    if (task) taskTaken = true;
    let values: unknown[] | undefined;
    if (task && boards?.values) { try { values = JSON.parse(boards.values); } catch { /* none */ } }
    else if (isTaskBlock && boards?.values && !boards?.task && !taskTaken) {
      // A task board seen after the student worked it - the teacher checking - shows what they set.
      try { values = JSON.parse(boards.values); taskTaken = true; } catch { /* none */ }
    }
    // Drawn again only when the block itself changed: a task board is being dragged.
    const drawnAs = `${task ? "task" : "still"}:${raw}`;
    if (el.getAttribute("data-board-drawn") === drawnAs) return;
    el.setAttribute("data-board-drawn", drawnAs);
    renderBoardBlock(el, raw, {
      task,
      values,
      onChange: task ? (v) => boards?.dotNet?.invokeMethodAsync("OnBoard", v) : undefined,
    });
  });
  (window as any).examHighlight?.run();
}

const examinRichEditor = {
  init(hostId: string, value: string, readOnly: boolean, dotNetRef: any, placeholder?: string, backupKey?: string, markdownPaste?: boolean, images?: string, mathPaste?: boolean): void {
    const shell = document.getElementById(hostId);
    // _hostId lives on the OUTER shell div (which also holds the toolbar and hidden file
    // input), not on the actual mount point — that's the nested .pdf-document-editable slot,
    // three levels down (shell > .pdf-document-paper > .pdf-document-page > .pdf-document-
    // editable). Passing `shell` itself to new Editor({element}) appends Tiptap's own div as
    // a sibling of the toolbar instead of populating the intended slot.
    const mountPoint = shell?.querySelector(".pdf-document-editable");
    if (!mountPoint) return;

    const state: EditorState = {
      editor: null as any, dotNetRef, notifyTimer: null, selectionTimer: null, lastActive: null,
      backupKey: backupKey || null, backupTimer: null,
      markdownPaste: Boolean(markdownPaste),
      mathPaste: Boolean(mathPaste),
      images: asPolicy(images),
    };
    const editor = new Editor({
      element: mountPoint as HTMLElement,
      extensions: buildExtensions(placeholder),
      content: value || "",
      editable: !readOnly,
      editorProps: {
        // A paste that is HTML (Word, a web page) is cleaned, and its text read for $formulas$ where the editor takes them.
        transformPastedHTML: (html: string) => (state.mathPaste ? convertMathInHtml(cleanPastedHtml(html)) : cleanPastedHtml(html)),
        // Plain text with formulas written between dollar signs: the formulas go in as formulas. Read here, as a slice with
        // open ends, so one line pasted in the middle of a paragraph stays in that paragraph, as any plain paste does. Not
        // for "paste as plain text" (Ctrl+Shift+V), which asks for the text as it is; and where the page converts Markdown on
        // the server (handlePaste, below) that path has taken the text before it gets here. Null: the editor's own reading.
        clipboardTextParser: (text: string, $context: ResolvedPos, plain: boolean, view: EditorView): Slice => {
          if (!state.mathPaste || plain || !hasMath(text)) return null as unknown as Slice;
          const holder = document.createElement("div");
          holder.innerHTML = textWithMathToHtml(text);
          return PMDOMParser.fromSchema(view.state.schema).parseSlice(holder, { preserveWhitespace: true, context: $context });
        },
        // Markdown pasted as plain text - an AI's answer copied with its copy button - goes to the
        // server to be made the editor's own HTML (formulas as formulas) and comes back in at the
        // caret. Rich HTML on the clipboard is left to the ordinary paste.
        // A file dropped in is a file from the computer: only where files may come in.
        handleDrop: (view, event, _slice, moved) => {
          const image = !moved && state.images === "all" ? imageOf(event.dataTransfer) : null;
          if (!image) return false;
          event.preventDefault();
          uploadImage(state, image, view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos);
          return true;
        },
        handlePaste: (view, event) => {
          const data = event.clipboardData;
          const copied = toolBoardOf(data);
          if (copied) {
            event.preventDefault();
            void (async () => {
              let board = copied.board;
              // A script's board cannot be drawn again here: its small picture is kept as an image of the answer.
              if (copied.picture && !board.spec && state.images !== "none") {
                try { const url = await storeImage(state, copied.picture); if (url) board = { ...board, image: url }; } catch { /* the board goes in without it */ }
              }
              state.editor.chain().focus().insertContent({ type: "boardState", attrs: { data: JSON.stringify(board) } }).run();
              notify(state, true);
            })();
            return true;
          }
          const image = (state.images !== "none" ? toolImageOf(data) : null)
            ?? (state.images === "paste" || state.images === "all" ? imageOf(data) : null);
          if (image) {
            event.preventDefault();
            uploadImage(state, image);
            return true;
          }
          const text = data?.getData("text/plain") ?? "";
          // Code: a code block of its own, indentation kept - unless the caret is in one already,
          // where the ordinary paste keeps it as it is.
          const inCode = view.state.selection.$from.parent.type.name === "codeBlock";
          if (!inCode && text && looksLikeCode(text, data?.getData("text/html") ?? "")) {
            event.preventDefault();
            const language = guessLanguage(text);
            state.editor.chain().focus().insertContent({
              type: "codeBlock",
              attrs: language ? { language } : {},
              content: [{ type: "text", text: text.replace(/\r\n/g, "\n").replace(/\n+$/, "") }],
            }).run();
            notify(state, true);
            return true;
          }
          if (!state.markdownPaste || !state.dotNetRef) return false;
          if (!text || data?.getData("text/html") || !looksLikeMarkdown(text)) return false;
          event.preventDefault();
          state.dotNetRef.invokeMethodAsync("ConvertMarkdown", text).then((html: string) => {
            if (!html) return;
            state.editor.chain().focus().insertContent(html).run();
            notify(state, true);
          });
          return true;
        },
      },
      onUpdate: () => { notify(state); backup(state); publishActive(state); },
      onSelectionUpdate: () => publishActive(state),
      // Focus and blur do not move the caret, but the toolbar should still settle to the
      // state of wherever the caret now is when focus returns.
      onFocus: () => publishActive(state, true),
    });
    state.editor = editor;

    // Where the formula under an event stands in the text, or null when the event is not on one.
    const formulaAt = (event: Event): { pos: number; latex: string } | null => {
      if (!state.editor.isEditable) return null;
      const field = (event.target as HTMLElement | null)?.closest?.("math-field");
      if (!field) return null;
      let pos: number;
      try { pos = state.editor.view.posAtDOM(field, 0); } catch { return null; }
      const node = state.editor.state.doc.nodeAt(pos);
      return node?.type.name === "mathField" ? { pos, latex: node.attrs.latex || "" } : null;
    };

    // A formula is one piece to the editor and not typed into, and MathLive keeps the mouse for itself:
    // what reaches it is handled there and goes no further. So these are listened to on the way down (the
    // capture phase), before MathLive sees them. A click picks the formula up whole, for the formula button
    // to open it for mending; a double click opens it at once: its LaTeX goes to the formula panel, with
    // where it stands, and replaceMath puts the mended one back. MathLive sends clicks of its own after a
    // real one, so a second click is told by the browser's count (detail), never by the time between them.
    const openFormula = (formula: { pos: number; latex: string }) =>
      state.dotNetRef?.invokeMethodAsync("OnEditMath", formula.latex, formula.pos);
    // A graph's edit button: its data and where it stands go to the editor's panel.
    mountPoint.addEventListener("examin-board-edit", (event) => {
      const { data, pos } = (event as CustomEvent).detail ?? {};
      if (typeof data === "string" && typeof pos === "number") state.dotNetRef?.invokeMethodAsync("OnEditBoard", data, pos);
    });
    mountPoint.addEventListener("click", (event) => {
      const formula = formulaAt(event);
      if (!formula) return;
      if ((event as MouseEvent).detail >= 2) { openFormula(formula); return; }
      state.editor.chain().focus().setNodeSelection(formula.pos).run();
    }, true);
    mountPoint.addEventListener("dblclick", (event) => {
      const formula = formulaAt(event);
      if (!formula) return;
      event.preventDefault();
      openFormula(formula);
    }, true);

    instances.set(hostId, state);
    publishActive(state, true);
  },

  sync(hostId: string, value: string, readOnly: boolean, images?: string): void {
    const state = instances.get(hostId);
    if (!state) return;
    // Where pictures may come from can change under a live editor - an activity locked meanwhile.
    state.images = asPolicy(images);
    // Only on an actual change, and silently: Tiptap's setEditable emits "update" by default,
    // which reports the content as changed - so every re-render of the page made every editor on
    // it report, which re-rendered the page, which made them report again, forever.
    if (state.editor.isEditable === readOnly) state.editor.setEditable(!readOnly, false);
    // Only replace content if it actually differs AND the editor isn't currently focused —
    // mirrors the old sync()'s dirty/focus guard, preventing a Blazor round-trip from
    // clobbering a cursor mid-edit.
    if (state.editor.getHTML() !== value && !state.editor.isFocused) {
      state.editor.commands.setContent(value);
    }
  },

  getHTML(hostId: string): string {
    return instances.get(hostId)?.editor.getHTML() ?? "";
  },

  setValue(hostId: string, value: string): void {
    instances.get(hostId)?.editor.commands.setContent(value);
  },

  // The deliberate replace, as against sync()'s guarded one. sync() will not touch a focused
  // editor — right for a binding push, which would otherwise clobber a caret mid-edit, and
  // wrong for a caller that means it, such as clearing the form after the question was added,
  // which is exactly when the teacher's hands are still in the editor.
  setContent(hostId: string, html: string): void {
    instances.get(hostId)?.editor.commands.setContent(html);
  },

  command(hostId: string, command: string, value?: string): boolean {
    const state = instances.get(hostId);
    if (!state) return false;
    const result = runCommand(state.editor, command, value);
    notify(state, true);
    publishActive(state, true);
    return result;
  },

  changeFontSize(hostId: string, delta: number): boolean {
    const state = instances.get(hostId);
    if (!state) return false;
    const result = changeFontSizeCmd(state.editor, delta);
    notify(state, true);
    publishActive(state, true);
    return result;
  },

  setDirection(hostId: string, dir: "rtl" | "ltr" | "auto"): boolean {
    const state = instances.get(hostId);
    if (!state) return false;
    const result = (state.editor.commands as any).setDirection(dir);
    notify(state, true);
    publishActive(state, true);
    return result;
  },

  insertMath(hostId: string, latex: string): void {
    const state = instances.get(hostId);
    if (!state) return;
    state.editor.chain().focus().insertContent({ type: "mathField", attrs: { latex } }).run();
    notify(state, true);
  },

  // The formula the caret has picked up - clicked once, so it is selected as a whole - with where it
  // stands, so the formula button can open it for mending instead of starting a new one. Null when the
  // selection is anything else.
  selectedMath(hostId: string): { latex: string; pos: number } | null {
    const state = instances.get(hostId);
    if (!state) return null;
    const selection = state.editor.state.selection as any;
    if (selection.node?.type.name !== "mathField") return null;
    return { latex: selection.node.attrs.latex || "", pos: selection.from };
  },

  // Puts a mended formula where the one that was opened stands - unless the text moved under it, in
  // which case the formula is not there any more and nothing is replaced (the caller then inserts it).
  replaceMath(hostId: string, pos: number, latex: string): boolean {
    const state = instances.get(hostId);
    if (!state) return false;
    const node = state.editor.state.doc.nodeAt(pos);
    if (node?.type.name !== "mathField") return false;
    state.editor.view.dispatch(state.editor.state.tr.setNodeMarkup(pos, undefined, { latex }));
    notify(state, true);
    return true;
  },

  // A board made in the editor's own panel: the spec comes from the template and its parameters (or the
  // JSON), the same way the board tool makes it. No code of the teacher's runs here.
  async boardData(config: any, mode: string): Promise<string | null> {
    try {
      // A script is kept as it was written and drawn in a frame of its own, where it is shown (see renderBoardBlock): there is no
      // spec to make from it here. It cannot be a task.
      if (config?.mode === "script") {
        const code = String(config.script ?? "");
        if (!code.trim()) return null;
        return JSON.stringify({ v: 1, script: code, values: [], size: { w: 560, h: 380 }, text: "", cfg: config, mode: mode === "explore" ? "explore" : "reference" });
      }
      await ensureBoardLibrary();
      const spec = (window as any).ExaminBoard.specOf(config);
      if (!spec) return null;
      const data: any = { v: 1, spec, values: [], size: { w: 560, h: 380 }, text: "", cfg: config };
      data.mode = mode === "task" || mode === "explore" ? mode : "reference";
      return JSON.stringify(data);
    } catch { return null; }
  },

  async insertBoard(hostId: string, config: any, mode: string): Promise<boolean> {
    const state = instances.get(hostId);
    const data = await examinRichEditor.boardData(config, mode);
    if (!state || !data) return false;
    state.editor.chain().focus().insertContent({ type: "boardState", attrs: { data } }).run();
    notify(state, true);
    return true;
  },

  // The graph at this place made again from the panel: the same block, with its new setting.
  async replaceBoard(hostId: string, pos: number, config: any, mode: string): Promise<boolean> {
    const state = instances.get(hostId);
    const data = await examinRichEditor.boardData(config, mode);
    if (!state || !data || state.editor.state.doc.nodeAt(pos)?.type.name !== "boardState") return false;
    state.editor.chain().command(({ tr }) => { tr.setNodeMarkup(pos, undefined, { data }); return true; }).run();
    notify(state, true);
    return true;
  },

  // The board as it will be, in the panel; false when the setting does not make one.
  async previewBoard(elementId: string, config: any, mode: string): Promise<boolean> {
    const host = document.getElementById(elementId);
    const data = await examinRichEditor.boardData(config, mode);
    if (!host || !data) { host?.replaceChildren(); return false; }
    host.removeAttribute("data-board-state");
    await renderBoardBlock(host, data, { live: true });
    return true;
  },

  insertMermaid(hostId: string, source: string): void {
    const state = instances.get(hostId);
    if (!state || !source?.trim()) return;
    state.editor.chain().focus().insertContent({ type: "mermaidDiagram", attrs: { source } }).run();
    notify(state, true);
  },

  async setMermaidPreview(elementId: string, source: string): Promise<void> {
    const host = document.getElementById(elementId);
    if (!host) return;
    await renderMermaidDiagram(host, source);
  },

  insertText(hostId: string, value: string): void {
    const state = instances.get(hostId);
    if (!state || !value?.trim()) return;
    const lines = value.split(/\r?\n/);
    const chain = state.editor.chain().focus();
    lines.forEach((line, i) => {
      chain.insertContent(line);
      if (i < lines.length - 1) chain.insertContent({ type: "hardBreak" });
    });
    chain.run();
    notify(state, true);
  },

  insertTemplate(hostId: string, html: string): void {
    const state = instances.get(hostId);
    if (!state) return;
    state.editor.chain().focus().insertContent(html).run();
    notify(state, true);
  },

  insertImage(hostId: string, url: string): void {
    const state = instances.get(hostId);
    if (!state) return;
    state.editor.chain().focus().setImage({ src: url, alt: "" }).run();
    notify(state, true);
  },

  insertTable(hostId: string, rows: number, columns: number): void {
    const state = instances.get(hostId);
    if (!state) return;
    state.editor.chain().focus().insertTable({ rows, cols: columns, withHeaderRow: true }).run();
    notify(state, true);
  },

  tableCommand(hostId: string, command: string): boolean {
    const state = instances.get(hostId);
    if (!state) return false;
    const result = tableCommandFn(state.editor, command);
    notify(state, true);
    return result;
  },

  // A panel of this kind round the paragraphs the selection touches. In a panel already: the same kind takes it off again,
  // another kind changes it.
  insertPanel(hostId: string, variant: string): boolean {
    const state = instances.get(hostId);
    if (!state) return false;
    const kind = normalizePanelVariant(variant);
    const editor = state.editor;
    let done: boolean;
    if (editor.isActive("panel")) {
      done = editor.isActive("panel", { variant: kind })
        ? editor.chain().focus().lift("panel").run()
        : editor.chain().focus().updateAttributes("panel", { variant: kind }).run();
    } else {
      done = editor.chain().focus().wrapIn("panel", { variant: kind }).run();
    }
    notify(state, true);
    return done;
  },

  // Two to four panels side by side, each an empty note to write in and to give its own kind.
  insertPanelRow(hostId: string, count: number): void {
    const state = instances.get(hostId);
    if (!state) return;
    const panels = Math.min(4, Math.max(2, Math.round(count) || 2));
    state.editor.chain().focus().insertContent({
      type: "panelRow",
      content: Array.from({ length: panels }, () => ({ type: "panel", attrs: { variant: "note" }, content: [{ type: "paragraph" }] })),
    }).run();
    notify(state, true);
  },

  insertCodeBlock(hostId: string, language?: string): void {
    const state = instances.get(hostId);
    if (!state) return;
    const { from, to, empty } = state.editor.state.selection;
    const selectedText = empty ? "" : state.editor.state.doc.textBetween(from, to, "\n");
    state.editor
      .chain()
      .focus()
      .insertContent({
        type: "codeBlock",
        attrs: language ? { language } : {},
        content: selectedText ? [{ type: "text", text: selectedText }] : [],
      })
      .run();
    notify(state, true);
  },

  // Tiptap/ProseMirror keeps its own selection state continuously and toolbar buttons already
  // use @onmousedown:preventDefault to avoid stealing focus, so there is no equivalent need for
  // the old editor's manual save/restore-selection dance. Kept as a no-op so the existing
  // Razor call sites (ToggleFormula/ToggleTable/etc.) don't need touching in this pass —
  // removing those calls entirely is a safe follow-up cleanup, not a correctness requirement.
  saveSelection(_hostId: string): void {},

  clickHiddenInput(inputId: string): void {
    (document.getElementById(inputId) as HTMLElement | null)?.click();
  },

  // The toolbar's picture button: a file chosen goes the way a pasted picture does - made smaller
  // first, then kept by the server.
  pickImage(hostId: string): void {
    const state = instances.get(hostId);
    if (!state || state.images !== "all") return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => { const file = input.files?.[0]; if (file?.type.startsWith("image/")) uploadImage(state, file); };
    input.click();
  },

  hydrateStatic,
  // Mermaid for other tools of the page (the SQL tool's diagram of tables).
  renderMermaid: renderMermaidDiagram,

  async setMathPreview(elementId: string, latex: string): Promise<void> {
    const host = document.getElementById(elementId);
    if (!host) return;
    await customElements.whenDefined("math-field");
    let field = host.querySelector("math-field") as any;
    if (!field) {
      field = document.createElement("math-field");
      field.setAttribute("aria-label", host.getAttribute("aria-label") || "Formula preview");
      host.replaceChildren(field);
    }
    field.readOnly = false;
    field.value = String(latex || "");
    field.readOnly = true;
    field.setAttribute("read-only", "");
  },

  dispose(hostId: string): void {
    const state = instances.get(hostId);
    if (!state) return;
    if (state.notifyTimer) clearTimeout(state.notifyTimer);
    if (state.backupTimer) {
      clearTimeout(state.backupTimer);
      try { localStorage.setItem(state.backupKey!, state.editor.getHTML()); } catch { }
    }
    state.editor.destroy();
    instances.delete(hostId);
  },
};

declare global {
  interface Window {
    examinRichEditor: typeof examinRichEditor;
  }
}

window.examinRichEditor = examinRichEditor;
